import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Save, Loader2, Trash2, Pencil } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

const SettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [matDialog, setMatDialog] = useState(null);
  const [matForm, setMatForm] = useState({ name: "", price: 0, unit: "piece", supplier_id: "", notes: "" });

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, sup, mat] = await Promise.all([
        api.get("/settings"), api.get("/suppliers"), api.get("/raw-materials")
      ]);
      setSettings(s.data); setSuppliers(sup.data); setMaterials(mat.data);
    } catch { toast.error("Failed to load settings"); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, sup, mat] = await Promise.all([
          api.get("/settings"), api.get("/suppliers"), api.get("/raw-materials")
        ]);
        if (!cancelled) { setSettings(s.data); setSuppliers(sup.data); setMaterials(mat.data); }
      } catch {
        if (!cancelled) toast.error("Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  // ===== Categories =====
  const addCategory = () => {
    const name = window.prompt("Category name (e.g. Tarpaulin)");
    if (!name) return;
    const slug = slugify(name);
    if (settings.categories.find((c) => c.slug === slug)) { toast.error("Category exists"); return; }
    setSettings({ ...settings, categories: [...settings.categories, { slug, name, finishing_options: [] }] });
  };
  const removeCategory = (slug) =>
    setSettings({ ...settings, categories: settings.categories.filter((c) => c.slug !== slug) });
  const addFinishing = (slug) => {
    const name = window.prompt("Finishing option name");
    if (!name) return;
    const key = slugify(name);
    setSettings({
      ...settings,
      categories: settings.categories.map((c) => c.slug === slug
        ? { ...c, finishing_options: c.finishing_options.includes(key) ? c.finishing_options : [...c.finishing_options, key] }
        : c)
    });
  };
  const removeFinishing = (slug, opt) => setSettings({
    ...settings,
    categories: settings.categories.map((c) => c.slug === slug
      ? { ...c, finishing_options: c.finishing_options.filter((o) => o !== opt) } : c)
  });

  // ===== Units =====
  const addUnit = () => {
    const name = window.prompt("Unit (e.g. per piece, per sq.ft)");
    if (!name) return;
    if (settings.units.includes(name)) return;
    setSettings({ ...settings, units: [...settings.units, name] });
  };
  const removeUnit = (u) => setSettings({ ...settings, units: settings.units.filter((x) => x !== u) });

  // ===== Payment options =====
  const addPay = () => {
    const name = window.prompt("Payment option (e.g. gcash)");
    if (!name) return;
    const k = slugify(name);
    if (settings.payment_options.includes(k)) return;
    setSettings({ ...settings, payment_options: [...settings.payment_options, k] });
  };
  const removePay = (p) => setSettings({ ...settings, payment_options: settings.payment_options.filter((x) => x !== p) });

  // ===== Raw Materials =====
  const openMat = (m) => {
    if (m) { setMatForm({ ...m, supplier_id: m.supplier_id || "" }); setMatDialog(m); }
    else { setMatForm({ name: "", price: 0, unit: "piece", supplier_id: "", notes: "" }); setMatDialog({}); }
  };
  const saveMat = async () => {
    try {
      const payload = { ...matForm, price: parseFloat(matForm.price) || 0, supplier_id: matForm.supplier_id || null };
      if (matDialog.material_id) await api.patch(`/raw-materials/${matDialog.material_id}`, payload);
      else await api.post("/raw-materials", payload);
      toast.success("Saved");
      setMatDialog(null);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeMat = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    try { await api.delete(`/raw-materials/${m.material_id}`); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (loading || !settings) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  return (
    <div className="p-6 lg:p-10 pav-fade-in" data-testid="settings-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Configuration</p>
          <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F] mt-1">Settings</h1>
          <p className="text-sm text-neutral-500 mt-1">Categories, units, finishing options, raw materials, payment methods and shipping rates.</p>
        </div>
        <button onClick={saveSettings} disabled={saving} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save settings
        </button>
      </div>

      <Tabs defaultValue="categories">
        <TabsList className="bg-[#F4F6FB] mb-5">
          <TabsTrigger value="categories" data-testid="tab-categories">Categories & Finishing</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-units">Units</TabsTrigger>
          <TabsTrigger value="materials" data-testid="tab-materials">Raw Materials</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payment Options</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping Rates</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" data-testid="categories-tab">
          <div className="pav-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Categories & finishing options</h2>
              <button onClick={addCategory} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="add-category-button">
                <Plus className="h-4 w-4" /> Add category
              </button>
            </div>
            <div className="space-y-4">
              {settings.categories.map((c) => (
                <div key={c.slug} className="border border-[#E2E8F0] rounded-xl p-4" data-testid={`category-${c.slug}`}>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div>
                      <h3 className="font-display font-bold">{c.name}</h3>
                      <p className="text-xs text-neutral-500">slug: {c.slug}</p>
                    </div>
                    <button onClick={() => removeCategory(c.slug)} data-testid={`remove-category-${c.slug}`} className="p-1.5 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    {c.finishing_options.map((opt) => (
                      <span key={opt} className="pav-chip flex items-center gap-1.5" data-testid={`finishing-${c.slug}-${opt}`}>
                        {opt.replace(/_/g, " ")}
                        <button onClick={() => removeFinishing(c.slug, opt)} className="text-neutral-500 hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <button onClick={() => addFinishing(c.slug)} data-testid={`add-finishing-${c.slug}`} className="text-xs text-[#0B1E3F] hover:underline font-semibold inline-flex items-center gap-1">
                      <Plus className="h-3 w-3" /> Add finishing
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="units" data-testid="units-tab">
          <div className="pav-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Units of measurement</h2>
              <button onClick={addUnit} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="add-unit-button">
                <Plus className="h-4 w-4" /> Add unit
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.units.map((u) => (
                <span key={u} className="pav-chip flex items-center gap-1.5" data-testid={`unit-${u}`}>
                  {u}
                  <button onClick={() => removeUnit(u)} className="text-neutral-500 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="materials" data-testid="materials-tab">
          <div className="pav-card p-0 overflow-hidden">
            <div className="p-5 border-b border-[#E2E8F0] flex items-center justify-between">
              <h2 className="font-display text-lg font-bold">Raw materials</h2>
              <button onClick={() => openMat(null)} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="add-material-button">
                <Plus className="h-4 w-4" /> Add material
              </button>
            </div>
            {materials.length === 0 ? (
              <p className="p-10 text-center text-neutral-500">No materials yet.</p>
            ) : (
              <table className="w-full">
                <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                  <tr>
                    <th className="text-left px-4 py-3">Material</th>
                    <th className="text-left px-4 py-3">Unit</th>
                    <th className="text-right px-4 py-3">Price</th>
                    <th className="text-left px-4 py-3">Supplier</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0] text-sm">
                  {materials.map((m) => (
                    <tr key={m.material_id} data-testid={`material-row-${m.material_id}`}>
                      <td className="px-4 py-3 font-semibold">{m.name}</td>
                      <td className="px-4 py-3 text-neutral-600">{m.unit}</td>
                      <td className="px-4 py-3 text-right font-display font-bold text-[#0B1E3F]">₱{(m.price || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-neutral-600">{m.supplier_name || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => openMat(m)} data-testid={`material-edit-${m.material_id}`} className="p-1.5 hover:bg-[#F4F6FB] rounded mr-1">
                          <Pencil className="h-4 w-4 text-[#0B1E3F]" />
                        </button>
                        <button onClick={() => removeMat(m)} data-testid={`material-delete-${m.material_id}`} className="p-1.5 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="payments" data-testid="payments-tab">
          <div className="pav-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold">Payment options</h2>
              <button onClick={addPay} className="btn-accent text-sm inline-flex items-center gap-2" data-testid="add-payment-button">
                <Plus className="h-4 w-4" /> Add option
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {settings.payment_options.map((p) => (
                <span key={p} className="pav-chip flex items-center gap-1.5 capitalize" data-testid={`payment-${p}`}>
                  {p.replace(/_/g, " ")}
                  <button onClick={() => removePay(p)} className="text-neutral-500 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="shipping" data-testid="shipping-tab">
          <div className="pav-card space-y-4">
            <h2 className="font-display text-lg font-bold">Shipping & delivery rates</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Within Metro Naga flat fee (₱)</label>
                <input type="number" min="0" step="1" className="pav-input mt-1.5"
                  data-testid="metro-naga-flat-fee-input"
                  value={settings.metro_naga_flat_fee}
                  onChange={(e) => setSettings({ ...settings, metro_naga_flat_fee: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Free km threshold</label>
                <input type="number" min="0" step="0.5" className="pav-input mt-1.5"
                  data-testid="free-km-threshold-input"
                  value={settings.free_km_threshold}
                  onChange={(e) => setSettings({ ...settings, free_km_threshold: parseFloat(e.target.value) || 0 })} />
                <p className="text-xs text-neutral-500 mt-1">Km included in the flat fee.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Rate per km in excess (₱)</label>
                <input type="number" min="0" step="0.5" className="pav-input mt-1.5"
                  data-testid="rate-per-km-input"
                  value={settings.rate_per_km_over_12}
                  onChange={(e) => setSettings({ ...settings, rate_per_km_over_12: parseFloat(e.target.value) || 0 })} />
                <p className="text-xs text-neutral-500 mt-1">Charged per km above the threshold.</p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Raw material dialog */}
      <Dialog open={!!matDialog} onOpenChange={(o) => !o && setMatDialog(null)}>
        <DialogContent className="max-w-md" data-testid="material-dialog">
          <DialogHeader><DialogTitle>{matDialog?.material_id ? "Edit material" : "Add raw material"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Material name</label>
              <input className="pav-input mt-1" data-testid="material-name-input"
                value={matForm.name} onChange={(e) => setMatForm({ ...matForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Price (₱)</label>
                <input type="number" min="0" step="0.01" className="pav-input mt-1" data-testid="material-price-input"
                  value={matForm.price} onChange={(e) => setMatForm({ ...matForm, price: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Price per unit</label>
                <input className="pav-input mt-1" placeholder="e.g. piece, roll, kg" data-testid="material-unit-input"
                  value={matForm.unit} onChange={(e) => setMatForm({ ...matForm, unit: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Supplier</label>
              <Select value={matForm.supplier_id || "none"} onValueChange={(v) => setMatForm({ ...matForm, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger className="pav-input mt-1" data-testid="material-supplier-select"><SelectValue placeholder="No supplier" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Notes</label>
              <textarea className="pav-input mt-1 min-h-[60px]" data-testid="material-notes-input"
                value={matForm.notes || ""} onChange={(e) => setMatForm({ ...matForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setMatDialog(null)} className="btn-outline text-sm">Cancel</button>
            <button onClick={saveMat} className="btn-primary text-sm" data-testid="material-save-button">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
